import React, { useEffect, useState } from 'react';
import { Alert, Grid } from '@mui/material';
import { EntityEditDialog, SelectInput } from '@lp/lp-ui';
import { useAppSelector } from '../../app/hooks';
import { useCurrentUser } from '../../app/useCurrentUser';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import RoleSelector from '../common/RoleSelector';

interface BulkAddToOrganizationDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (organizationId: number, roleIds: number[]) => void;
}

const BulkAddToOrganizationDialog: React.FC<BulkAddToOrganizationDialogProps> = ({ open, onClose, onSave }) => {
    const currentUser = useAppSelector(store => store.currentUser);
    const roles = useCache(CachedEntity.Role);
    const [, isTenantAdmin] = useCurrentUser();
    const [selectedOrgId, setSelectedOrgId] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    // Non-tenant admins: auto-lock to their own organization
    useEffect(() => {
        if (open && !isTenantAdmin && currentUser.data?.organization) {
            setSelectedOrgId(currentUser.data.organization.id);
        }
    }, [open, isTenantAdmin, currentUser.data?.organization]);

    const orgOptions = [
        { value: 0, label: '-' },
        ...(currentUser.data?.organizations
            ? currentUser.data.organizations.map(o => ({ value: o.id, label: o.displayName }))
            : [])
    ];

    const handleOrgChange = (e: React.ChangeEvent<any>) => {
        setSelectedOrgId(e.target.value);
    };

    const handleClose = () => {
        setSelectedOrgId(0);
        setSelectedIds([]);
        onClose();
    };

    const handleSave = () => {
        onSave(selectedOrgId, selectedIds);
        setSelectedOrgId(0);
        setSelectedIds([]);
    };

    return (
        <EntityEditDialog
            open={open}
            maxWidth="md"
            fullWidth
            title="Add to Organization"
            handleClose={handleClose}
            handleSave={handleSave}
            editing={true}
            disableSave={selectedOrgId === 0}
        >
            <Alert severity="info" sx={{ mb: 2 }}>
                This action will be executed in the background and it may take some time for all changes to take effect.
            </Alert>
            <Grid container spacing={0} columnSpacing={6}>
                <Grid item xs={12}>
                    <SelectInput
                        label="Organization"
                        isEditing={isTenantAdmin}
                        name="bulkOrganizationId"
                        options={orgOptions}
                        onChange={handleOrgChange}
                        onBlur={() => {}}
                        helperText=""
                        value={selectedOrgId}
                    />
                </Grid>
                {selectedOrgId > 0 && (
                    <Grid item xs={12}>
                        <RoleSelector
                            roles={roles}
                            selectedIds={selectedIds}
                            onChange={setSelectedIds}
                            namePrefix="bulk-org-role"
                            height={380}
                        />
                    </Grid>
                )}
            </Grid>
        </EntityEditDialog>
    );
};

export default BulkAddToOrganizationDialog;
